import { createElement, forwardRef } from 'react';
import type { ForwardedRef, JSX } from 'react';

import classNames from 'classnames';
import type { Link, LinkProps } from 'react-router-dom';

import * as styles from './Button.module.scss';

const sizeStyleMap = {
  large: styles.sizeLarge,
  medium: styles.sizeMedium,
  small: styles.sizeSmall,
} as const;

const variantStyleMap = {
  contained: styles.variantContained,
  blurred: styles.variantBlurred,
  text: styles.variantText,
  outlined: styles.variantOutlined,
} as const;

export type ButtonComponentType = keyof JSX.IntrinsicElements | typeof Link;

export type ButtonProps<T extends ButtonComponentType = 'button'> = {
  component?: T;
  variant?: keyof typeof variantStyleMap;
  color?: 'primary' | 'subscription';
  size?: keyof typeof sizeStyleMap;
  active?: boolean;
  isActiveBrandColor?: boolean;
  rounded?: boolean;
  round?: boolean;
  className?: string;
  disabled?: boolean;
  ['data-testid']?: string;
} & (T extends keyof JSX.IntrinsicElements
  ? JSX.IntrinsicElements[T]
  : LinkProps);

const BaseButton = <T extends ButtonComponentType = 'button'>(
  {
    variant = 'outlined',
    color,
    size = 'small',
    children,
    active,
    isActiveBrandColor,
    rounded,
    component,
    className,
    round,
    ...props
  }: ButtonProps<T>,
  ref?: ForwardedRef<any>
) => {
  const labelClassName = classNames(
    styles.button,
    className,
    variantStyleMap[variant],
    sizeStyleMap[size],
    {
      [styles.disabled]: props.disabled,
      [styles.active]: active,
      [styles.isActiveBrandColor]: isActiveBrandColor,
      [styles.rounded]: rounded,
      [styles.round]: round,
    }
  );

  return createElement(
    component ?? 'button',
    // @ts-ignore
    { className: labelClassName, ref, ...props, 'data-nosnippet': '' },
    children
  );
};

export const Button = forwardRef(BaseButton) as unknown as <
  T extends ButtonComponentType = 'button',
>(
  props: ButtonProps<T>
) => ReturnType<typeof BaseButton>;
